<script lang="ts">
  interface Team {
    slug: string;
    name: string;
    day_of_week: string | null;
    time_slot: string | null;
    description: string | null;
    kakao_url: string | null;
    kakao_code: string | null;
    member_count: number;
  }
  interface Member {
    id: string;
    name: string | null;
    threads: string | null;
    tier: 'blue' | 'green' | 'yellow' | null;
    score: number | null;
    phone: string | null;
    role: string | null;
  }
  interface Props { team: Team; members: Member[]; }
  let { team, members }: Props = $props();

  const dayKor: Record<string, string> = {
    mon: '월', tue: '화', wed: '수', thu: '목', fri: '금', sat: '토', sun: '일'
  };
  const slotKor: Record<string, string> = {
    morning: '오전', afternoon: '오후', evening: '저녁', weekend: '주말'
  };
  const tierMeta: Record<string, { label: string; cls: string }> = {
    blue:   { label: '🔵 Blue',   cls: 'bg-blue-100 text-blue-800' },
    green:  { label: '🟢 Green',  cls: 'bg-emerald-100 text-emerald-800' },
    yellow: { label: '🟡 Yellow', cls: 'bg-amber-100 text-amber-800' }
  };
  function dayLabel(): string {
    return [team.day_of_week ? dayKor[team.day_of_week] : '',
            team.time_slot   ? slotKor[team.time_slot]   : '']
      .filter(Boolean).join(' ');
  }
  function fmtPhone(raw: string | null): string {
    if (!raw) return '';
    const d = raw.replace(/\D/g, '');
    return d.length === 11 ? `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}` : raw;
  }
</script>

<div class="p-6 space-y-6">
  <header class="flex items-start justify-between gap-4">
    <div>
      <nav class="text-sm text-gray-500 mb-2">
        <a href="/admin/community/study_teams" class="hover:underline">스터디 팀</a>
        <span class="mx-1">›</span>
        <span class="text-gray-700">{team.name}</span>
      </nav>
      <h1 class="text-2xl font-bold flex items-center gap-3">
        {team.name}
        <span class="px-2 py-0.5 bg-blue-100 text-blue-800 text-sm rounded">{team.member_count}명</span>
      </h1>
      {#if dayLabel()}<div class="text-sm text-gray-600 mt-1">📅 {dayLabel()}</div>{/if}
      {#if team.description}<p class="text-sm text-gray-500 mt-1">{team.description}</p>{/if}
    </div>
    <div class="flex gap-2">
      <a href="/admin/community/study_teams/{team.slug}/attendance"
         class="text-sm px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">📅 출석 캘린더</a>
      {#if team.kakao_url}
        <a href={team.kakao_url} target="_blank" rel="noopener noreferrer"
           class="text-sm px-3 py-2 border border-gray-300 rounded hover:bg-gray-50">💬 오픈톡방</a>
      {/if}
    </div>
  </header>

  <section class="bg-white border rounded-lg overflow-hidden">
    <div class="px-4 py-3 border-b bg-gray-50 text-sm font-semibold">멤버 ({members.length}명)</div>
    {#if members.length === 0}
      <div class="p-8 text-center text-gray-500">아직 배정된 멤버가 없습니다.</div>
    {:else}
      <table class="w-full text-sm">
        <thead class="bg-gray-50 text-gray-600">
          <tr>
            <th class="px-4 py-2 text-left w-10">#</th>
            <th class="px-4 py-2 text-left">이름</th>
            <th class="px-4 py-2 text-left">Tier</th>
            <th class="px-4 py-2 text-left">직군</th>
            <th class="px-4 py-2 text-left">Threads</th>
            <th class="px-4 py-2 text-left">연락처</th>
            <th class="px-4 py-2 text-right">점수</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          {#each members as m, i}
            {@const tm = m.tier ? tierMeta[m.tier] : null}
            <tr class="hover:bg-gray-50">
              <td class="px-4 py-2 text-gray-400">{i + 1}</td>
              <td class="px-4 py-2 font-medium">
                <a href="/admin/community/surveys/vibestudy-pilot-1/responses/{m.id}"
                   class="hover:underline text-gray-900">{m.name || '(이름 미상)'}</a>
              </td>
              <td class="px-4 py-2">
                {#if tm}<span class="px-2 py-0.5 text-xs rounded {tm.cls}">{tm.label}</span>{/if}
              </td>
              <td class="px-4 py-2 text-gray-700">{m.role || '-'}</td>
              <td class="px-4 py-2 text-gray-600">
                {#if m.threads}
                  <a href="https://www.threads.net/@{m.threads}" target="_blank" rel="noopener noreferrer"
                     class="hover:underline">@{m.threads}</a>
                {:else}-{/if}
              </td>
              <td class="px-4 py-2 text-gray-600 font-mono text-xs">{fmtPhone(m.phone)}</td>
              <td class="px-4 py-2 text-right text-gray-700">{m.score ?? '-'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  </section>
</div>
